// frontend/src/pages/HomePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme, t } from '../contexts/ThemeContext';

// Scroll-triggered reveal - sections fade/rise into view as the user scrolls
// to them instead of firing once on page load, regardless of scroll position.
const revealVariant = {
	hidden: { opacity: 0, y: 28 },
	visible: { opacity: 1, y: 0 },
};
const revealTransition = { duration: 0.6, ease: [0.4, 0, 0.2, 1] as const };
const staggerParent = {
	visible: { transition: { staggerChildren: 0.07 } },
};

const EXAMPLES = [
	'French breakfast and coffee in Boston',
	'Art galleries and wine bars in NYC',
	'Hidden gem bars and live music in Nashville',
	'Jazz clubs and late-night bites in Chicago',
	'Parks, bookshops and brunch in San Francisco',
	'Rooftop bars and street food in Austin',
];

const AGENTS = [
	{ icon: '📍', label: 'Location' },
	{ icon: '🤔', label: 'Intent' },
	{ icon: '🔍', label: 'Venue Scout' },
	{ icon: '🔬', label: 'Research' },
	{ icon: '🗺️', label: 'Routing' },
	{ icon: '✨', label: 'Creator' },
];

const FEATURES = [
	{ icon: '⚡', title: 'Live Research', desc: 'Tavily API fetches real-time hours, reviews, and tips for every venue.' },
	{ icon: '🧠', title: '6 AI Agents', desc: 'LangGraph coordinates location, intent, scouting, research, routing, and creation.' },
	{ icon: '🗺️', title: 'Smart Routing', desc: 'Google Maps optimises your stop order and injects per-step transit directions.' },
	{ icon: '🎯', title: 'Personalised', desc: 'RAG system learns from your saved adventures to surface things you\'ll love.' },
];

const CITIES = [
	{ emoji: '🏛️', city: 'Boston', desc: 'Back Bay · North End · Cambridge · Beacon Hill' },
	{ emoji: '🗽', city: 'New York City', desc: 'Manhattan · Brooklyn · SoHo · East Village' },
	{ emoji: '🎸', city: 'Chicago', desc: 'River North · Wicker Park · Lincoln Park · Loop' },
	{ emoji: '🌴', city: 'Los Angeles', desc: 'Silver Lake · Venice · Downtown · Culver City' },
	{ emoji: '🌉', city: 'San Francisco', desc: 'Mission · Hayes Valley · North Beach · Castro' },
	{ emoji: '🎵', city: 'Nashville', desc: 'East Nashville · The Gulch · 12 South · Germantown' },
];

const HomePage: React.FC = () => {
	const { isAuthenticated } = useAuth();
	const { isDark } = useTheme();
	const tk = t(isDark);
	const navigate = useNavigate();

	const [exampleIdx, setExampleIdx] = useState(0);
	const [fade, setFade] = useState(true);
	const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
	const heroRef = useRef<HTMLDivElement>(null);

	// Cycle examples
	useEffect(() => {
		const timer = setInterval(() => {
			setFade(false);
			setTimeout(() => { setExampleIdx(i => (i + 1) % EXAMPLES.length); setFade(true); }, 300);
		}, 3200);
		return () => clearInterval(timer);
	}, []);

	// Scroll-linked parallax - useScroll/useTransform drive the blobs directly
	// off a motion value instead of a React state update on every scroll pixel.
	const { scrollY } = useScroll();
	const blob1Y = useTransform(scrollY, [0, 1200], [0, 180]);
	const blob2Y = useTransform(scrollY, [0, 1200], [0, -120]);
	const blob3Y = useTransform(scrollY, [0, 1200], [0, 96]);

	return (
		<div style={{
			minHeight: '100vh',
			background: tk.pageBg,
			position: 'relative',
			overflow: 'hidden',
			color: tk.textPrimary,
			fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
		}}>
			{/* Parallax blobs - scroll-linked via motion values, not React state */}
			<motion.div style={{
				position: 'fixed', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none', zIndex: 0,
				top: '-15%', left: '-10%', width: 600, height: 600,
				background: tk.blob1,
				y: blob1Y,
				transition: 'background 0.5s',
			}} />
			<motion.div style={{
				position: 'fixed', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none', zIndex: 0,
				bottom: '-20%', right: '-10%', width: 700, height: 700,
				background: tk.blob2,
				y: blob2Y,
				transition: 'background 0.5s',
			}} />
			<motion.div style={{
				position: 'fixed', borderRadius: '50%', filter: 'blur(120px)', pointerEvents: 'none', zIndex: 0,
				top: '40%', left: '30%', width: 400, height: 400,
				background: isDark ? 'rgba(236,72,153,0.08)' : 'rgba(236,72,153,0.05)',
				y: blob3Y,
			}} />

			<div ref={heroRef} style={{
				position: 'relative', zIndex: 1,
				maxWidth: 960, margin: '0 auto',
				padding: '80px 24px 60px',
				display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 56,
			}}>
				{/* ── HERO ── */}
				<div style={{ textAlign: 'center', maxWidth: 760, animation: 'fadeInUp 0.7s ease both' }}>
					<div style={{
						display: 'inline-block',
						background: isDark ? 'rgba(94,234,212,0.15)' : 'rgba(20,184,166,0.1)',
						border: `1px solid ${isDark ? 'rgba(94,234,212,0.35)' : 'rgba(20,184,166,0.25)'}`,
						color: tk.textAccent,
						borderRadius: 999, padding: '6px 18px',
						fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.04em', marginBottom: 24,
					}}>
						🗺️ AI-Powered Adventure Planning
					</div>

					<h1 style={{
						fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
						fontWeight: 800, lineHeight: 1.15, marginBottom: 20, letterSpacing: '-1px',
						color: tk.textPrimary,
					}}>
						Not recommendations.
						<span style={{
							background: tk.gradientAccent,
							WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
						}}> Adventures.</span>
					</h1>

					<p style={{ fontSize: '1.1rem', color: tk.textSecondary, lineHeight: 1.75, marginBottom: 32, maxWidth: 600, margin: '0 auto 32px' }}>
						Tell MiniQuest what you're in the mood for. Six AI agents find real venues,
						research them live, and hand you a complete half-day itinerary -
						sequenced, routed, and ready to walk out the door.
						Works in{' '}
						<strong style={{ color: tk.textAccent }}>any US city</strong>.
					</p>

					{/* Animated example */}
					<div style={{
						display: 'inline-flex', alignItems: 'center', gap: 10,
						background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
						border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
						borderRadius: 12, padding: '10px 20px', marginBottom: 36, fontSize: '0.92rem',
					}}>
						<span style={{ color: tk.textAccent, fontWeight: 700, whiteSpace: 'nowrap' }}>Try:</span>
						<span style={{ color: tk.textSecondary, fontStyle: 'italic', opacity: fade ? 1 : 0, transition: 'opacity 0.3s' }}>
							"{EXAMPLES[exampleIdx]}"
						</span>
					</div>

					{/* CTAs */}
					<div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
						<button
							onClick={() => navigate(isAuthenticated ? '/app' : '/register')}
							style={{
								background: tk.gradientAccent,
								color: 'white', border: 'none', borderRadius: 14,
								padding: '15px 34px', fontSize: '1.05rem', fontWeight: 700,
								cursor: 'pointer', boxShadow: '0 4px 24px rgba(20,184,166,0.45)',
								transition: 'all 0.2s',
							}}
							onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(20,184,166,0.55)'; }}
							onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(20,184,166,0.45)'; }}
						>
							🚀 {isAuthenticated ? 'Start Exploring' : 'Get Started - Free'}
						</button>
						<button
							onClick={() => navigate('/about')}
							style={{
								background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
								color: tk.textSecondary,
								border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'}`,
								borderRadius: 14, padding: '15px 28px', fontSize: '1.05rem', fontWeight: 600,
								cursor: 'pointer', transition: 'all 0.2s',
							}}
							onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
							onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
						>
							How it works →
						</button>
					</div>
				</div>

				{/* ── CITY CARDS ── */}
				<motion.div
					style={{ width: '100%' }}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: '-80px' }}
					variants={staggerParent}
				>
					<div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', color: tk.textAccent, textTransform: 'uppercase', marginBottom: 16, textAlign: 'center' }}>
						Works anywhere in the US
					</div>
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
						{CITIES.map(({ emoji, city, desc }) => (
							<motion.div key={city} variants={revealVariant} transition={revealTransition} style={{
								background: tk.cardBg,
								border: `1px solid ${tk.cardBorder}`,
								borderRadius: 16, padding: '16px 20px',
								display: 'flex', alignItems: 'center', gap: 14,
								backdropFilter: 'blur(12px)',
								transition: 'all 0.25s',
							}}
								onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(20,184,166,0.15)'; (e.currentTarget as HTMLDivElement).style.borderColor = tk.textAccent; }}
								onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'none'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; (e.currentTarget as HTMLDivElement).style.borderColor = tk.cardBorder; }}
							>
								<span style={{ fontSize: '1.8rem', flexShrink: 0 }}>{emoji}</span>
								<div>
									<div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 3, color: tk.textPrimary }}>{city}</div>
									<div style={{ fontSize: '0.76rem', color: tk.textMuted, lineHeight: 1.5 }}>{desc}</div>
								</div>
							</motion.div>
						))}
					</div>
					<div style={{ textAlign: 'center', marginTop: 12, fontSize: '0.78rem', color: tk.textMuted }}>
						+ every other US city and town
					</div>
				</motion.div>

				{/* ── AGENT PIPELINE ── */}
				<motion.div
					style={{ width: '100%', textAlign: 'center' }}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: '-80px' }}
					variants={staggerParent}
				>
					<div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', color: tk.textAccent, textTransform: 'uppercase', marginBottom: 18 }}>
						6-agent pipeline
					</div>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 6 }}>
						{AGENTS.map(({ icon, label }, i) => (
							<React.Fragment key={label}>
								<motion.div variants={revealVariant} transition={revealTransition} style={{
									display: 'flex', alignItems: 'center', gap: 7,
									background: isDark ? 'rgba(94,234,212,0.12)' : 'rgba(20,184,166,0.08)',
									border: `1px solid ${isDark ? 'rgba(94,234,212,0.25)' : 'rgba(20,184,166,0.18)'}`,
									borderRadius: 999, padding: '8px 16px',
									fontSize: '0.82rem', fontWeight: 600, color: tk.textSecondary,
									transition: 'all 0.2s',
								}}>
									<span style={{ fontSize: '1.1rem' }}>{icon}</span>
									{label}
								</motion.div>
								{i < AGENTS.length - 1 && (
									<span style={{ color: tk.textMuted, fontSize: '1.1rem' }}>→</span>
								)}
							</React.Fragment>
						))}
					</div>
				</motion.div>

				{/* ── FEATURE GRID ── */}
				<motion.div
					style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, width: '100%' }}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: '-80px' }}
					variants={staggerParent}
				>
					{FEATURES.map(({ icon, title, desc }, i) => (
						// Outer motion.div owns the scroll-reveal transform; the inner div keeps
						// its existing hover-lift transform untouched - two different DOM nodes,
						// so framer-motion's variant-driven transform never fights the hover one.
						<motion.div key={title} variants={revealVariant} transition={revealTransition}>
							<div
								onMouseEnter={() => setHoveredFeature(i)}
								onMouseLeave={() => setHoveredFeature(null)}
								style={{
									background: tk.cardBg,
									border: `1px solid ${hoveredFeature === i ? tk.textAccent : tk.cardBorder}`,
									borderRadius: 18, padding: '28px 22px',
									backdropFilter: 'blur(12px)',
									transition: 'all 0.25s',
									transform: hoveredFeature === i ? 'translateY(-4px)' : 'none',
									boxShadow: hoveredFeature === i ? '0 12px 32px rgba(20,184,166,0.15)' : 'none',
									cursor: 'default',
								}}
							>
								<div style={{ fontSize: '2rem', marginBottom: 12 }}>{icon}</div>
								<div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8, color: tk.textPrimary }}>{title}</div>
								<div style={{ fontSize: '0.83rem', color: tk.textMuted, lineHeight: 1.6 }}>{desc}</div>
							</div>
						</motion.div>
					))}
				</motion.div>

				{/* ── TECH STACK ── */}
				<motion.div
					style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: '-60px' }}
					variants={staggerParent}
				>
					{['LangGraph', 'Tavily API', 'OpenAI GPT-4o', 'Google Maps', 'MongoDB', 'FastAPI', 'Redis', 'ChromaDB'].map(tech => (
						<motion.span key={tech} variants={revealVariant} transition={revealTransition} style={{
							background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
							border: `1px solid ${isDark ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.08)'}`,
							color: tk.textMuted,
							borderRadius: 999, padding: '5px 14px',
							fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em',
							transition: 'all 0.2s',
						}}
							onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.color = tk.textAccent; (e.currentTarget as HTMLSpanElement).style.borderColor = tk.textAccent; }}
							onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.color = tk.textMuted; (e.currentTarget as HTMLSpanElement).style.borderColor = isDark ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.08)'; }}
						>
							{tech}
						</motion.span>
					))}
				</motion.div>
			</div>

			<style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
		</div>
	);
};

export default HomePage;